import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import {
    ZtButtonConfig,
    ZtButtonType,
} from 'src/app/shared/components/atoms/zt-button/zt-button.model';
import { ZtIcon } from 'src/app/shared/components/atoms/zt-icon/zt-icon.model';
import {
    ZtFilterConfig,
    ZtFilterEvent,
    ZtFilterEventType,
} from '../zt-filter/zt-filter.model';
import { filterFunctionMap } from './zt-table.filter';

import {
    ZtTableAction,
    ZtTableFilterConfig,
    ZtTableFilteredItem,
    ZtTableHeaderInfo,
    ZtTableHeaderType,
    ZtTableRow,
    ZtTableSortOrder,
} from './zt-table.model';
import { sortMap } from './zt-table.sort';

@Component({
    selector: 'zt-table',
    templateUrl: 'zt-table.component.html',
    styleUrls: ['zt-table.component.scss'],
})
export class ZtTableComponent<T extends ZtTableRow> implements OnChanges {
    @Input() headerColsInfo!: ZtTableHeaderInfo[];
    @Input() data: T[] = [];
    @Input() sortBy: string | null = null;
    @Input() sortOrder: ZtTableSortOrder = ZtTableSortOrder.DESC;
    @Input() hasFilters: boolean = false;
    @Input() filterConfig: ZtTableFilterConfig | null = null;

    readonly dateFormat: string = 'dd.MM.yyyy H:mm:ss';

    filteredData: T[] = [];
    startIndex: number = 0;
    maxIndex: number = 0;
    currentPage: number = 0;
    pages: number[] = [1];
    nrOfElementsPerPage: number = 20;
    sortedColumn: ZtTableHeaderInfo | null = null;
    filteredColKey: string | null = null;
    filteredLabelMap: Map<string, string> = new Map();
    filterConfigurationMap: Map<string, ZtFilterConfig> = new Map();
    activeFilterMap: Map<string, ZtTableFilteredItem> = new Map();

    playBtnConfig: ZtButtonConfig = {
        label: 'Play',
        icon: ZtIcon.PLAY,
        name: 'play',
    };
    downloadBtnConfig: ZtButtonConfig = {
        icon: ZtIcon.DOWNLOAD,
        name: 'download',
    };
    deleteBtnConfig: ZtButtonConfig = {
        icon: ZtIcon.DELETE,
        name: 'Delete',
    };

    constructor() {}

    ngOnChanges(changes: SimpleChanges): void {
        this.filteredData = this.data;
        this.startIndex = 0;
        this.preparePages();
        this.resetFilters();
        this.sortOnChanges();
    }

    resetFilters(): void {
        this.activeFilterMap = new Map();
        this.filteredColKey = null;
        this.headerColsInfo.forEach((h) => {
            let values;
            switch (h.type) {
                case this.ZtHeaderTypes.STRING_VARIANTS:
                    values = [
                        ...new Set(this.data.map((d) => d[h.key as keyof T])),
                    ];
                    break;
            }

            if (h.filterable && this.filterConfig?.column === h.key) {
                this.filterConfigurationMap.set(h.key, {
                    key: h.key,
                    type: h.type,
                    data: values,
                    selected: this.filterConfig?.value,
                } as ZtFilterConfig);

                this.onFilterChange({
                    key: h.key,
                    eventType: ZtFilterEventType.MATCH,
                    dataType: h.type,
                    data: this.filterConfig?.value,
                } as ZtFilterEvent);
            } else {
                this.filterConfigurationMap.set(h.key, {
                    key: h.key,
                    type: h.type,
                    data: values,
                    selected: this.filterConfig?.value,
                } as ZtFilterConfig);
            }
        });
    }

    onFilterChange(event: ZtFilterEvent): void {
        this.startIndex = 0;
        this.currentPage = 0;
        switch (event.eventType) {
            case ZtFilterEventType.NO_MATCH:
                this.filteredData = [];
                return;
            case ZtFilterEventType.ALL_MATCH:
                this.activeFilterMap.delete(event.key);
                break;
            case ZtFilterEventType.MATCH:
                this.activeFilterMap.set(event.key, {
                    data: event.data,
                    type: event.dataType,
                });
                break;
        }

        let filteredData = this.data;
        this.activeFilterMap.forEach(
            (item: ZtTableFilteredItem, key: string) => {
                const fitlerFunction = filterFunctionMap.get(item.type);
                if (fitlerFunction) {
                    filteredData = filteredData.filter(
                        fitlerFunction(key, item.data)
                    );
                }
            }
        );

        this.filteredData = filteredData;
        this.preparePages();

        if (this.sortedColumn) {
            this.sort(this.sortedColumn);
        }
    }

    sortOnChanges(): void {
        if (this.sortBy == null) {
            return;
        }

        const colToSort = this.headerColsInfo.filter(
            (c) => c.key === this.sortBy
        )[0];

        if (colToSort === null || !colToSort?.sortable) {
            return;
        }

        this.sort(colToSort);
    }

    onSortClick(clickedHeader: ZtTableHeaderInfo): void {
        if (clickedHeader.key === this.sortBy) {
            this.sortOrder =
                this.sortOrder === ZtTableSortOrder.ASC
                    ? ZtTableSortOrder.DESC
                    : ZtTableSortOrder.ASC;
        } else {
            this.sortBy = clickedHeader.key;
            this.sortOrder = ZtTableSortOrder.DESC;
        }

        this.sort(clickedHeader);
    }

    getColumnSortIcon(key: string): ZtIcon {
        if (key !== this.sortBy) {
            return ZtIcon.ARROWS_UP_DOWN;
        }

        return this.sortOrder === ZtTableSortOrder.DESC
            ? ZtIcon.FILTER_DESC
            : ZtIcon.FILTER_ASC;
    }

    sort(header: ZtTableHeaderInfo): void {
        const sortFunction = sortMap.get(header.type);

        if (sortFunction === null) {
            return;
        }

        this.filteredData.sort(sortFunction!(header.key, this.sortOrder));
        this.sortedColumn = header;
    }

    toString(headerColInfo: ZtTableHeaderInfo, item: T): string {
        if (headerColInfo.type === ZtTableHeaderType.NUMBER) {
            const parsedNr = Number.parseInt(
                item[headerColInfo.key as keyof ZtTableRow]
            );
            if (!Number.isNaN(parsedNr)) {
                return parsedNr.toFixed();
            } else {
                return 'NaN';
            }
        }

        if (headerColInfo.shouldConcat) {
            return item[headerColInfo.key as keyof ZtTableRow].value;
        }

        return item[headerColInfo.key as keyof ZtTableRow];
    }

    getConcatDisplayText(text: string): string {
        const fragments = text.split(' ');
        return fragments.length > 5
            ? fragments.slice(0, 5).join(' ').concat('...')
            : fragments.join(' ');
    }

    expandText(headerColInfo: ZtTableHeaderInfo, item: T, event: Event): void {
        if (event.target != null) {
            const tableCell = (event.target! as HTMLElement).parentElement;
            if (tableCell) {
                item[headerColInfo.key as keyof ZtTableRow].isConcated = false;
            }
        }
    }

    clipText(headerColInfo: ZtTableHeaderInfo, item: T, event: Event): void {
        if (event.target != null) {
            const tableCell = (event.target! as HTMLElement).parentElement;
            if (tableCell) {
                item[headerColInfo.key as keyof ZtTableRow].isConcated = true;
            }
        }
    }

    getBlob(headerColInfo: ZtTableHeaderInfo, item: T): Blob | undefined {
        if (headerColInfo.type === ZtTableHeaderType.AUDIO) {
            return item[headerColInfo.key as keyof ZtTableRow]?.blob;
        }

        return undefined;
    }

    getActions(headerColInfo: ZtTableHeaderInfo, item: T): ZtTableAction[] {
        return item[headerColInfo.key as keyof ZtTableRow];
    }

    onClickHandler(headerColInfo: ZtTableHeaderInfo, item: T): void {
        if (headerColInfo.type === ZtTableHeaderType.AUDIO) {
            item[headerColInfo.key as keyof ZtTableRow]?.action(item);
        }
    }

    onActionClickHandler(item: T, action: ZtTableAction): void {
        const callback = action.action;
        if (callback) {
            callback(item);
        }
    }

    shouldShowControls(headerColInfo: ZtTableHeaderInfo, item: T): boolean {
        return item[headerColInfo.key as keyof ZtTableRow].showControls;
    }

    shouldPlayAudio(headerColInfo: ZtTableHeaderInfo, item: T): boolean {
        return item[headerColInfo.key as keyof ZtTableRow].playAudio;
    }

    stopAudio(headerColInfo: ZtTableHeaderInfo, item: T): void {
        item[headerColInfo.key as keyof ZtTableRow].playAudio = false;
    }

    isConcated(headerColInfo: ZtTableHeaderInfo, item: T): boolean {
        return item[headerColInfo.key as keyof ZtTableRow].isConcated;
    }

    showConcatExpandBtn(text: string): boolean {
        const fragments = text.split(' ');
        return fragments.length > 5;
    }

    increasePage(): void {
        if (this.currentPage != this.maxIndex) {
            this.currentPage += 1;
            this.startIndex = this.currentPage * this.nrOfElementsPerPage;
        }
    }

    decreasePage(): void {
        if (this.currentPage != 0) {
            this.currentPage -= 1;
            this.startIndex = this.currentPage * this.nrOfElementsPerPage;
        }
    }

    switchPage(pageNr: number): void {
        if (pageNr !== this.currentPage) {
            this.currentPage = pageNr;
            this.startIndex = pageNr * this.nrOfElementsPerPage;
        }
    }

    private preparePages(): void {
        const totalPages = Math.ceil(
            this.filteredData.length / this.nrOfElementsPerPage
        );

        if (totalPages == 0) {
            this.pages = [1];
            this.maxIndex = 1;
            return;
        }

        this.maxIndex = totalPages - 1;
        this.pages = [...Array(totalPages)].map((v, i) => i + 1);
    }

    get ZtIcon(): typeof ZtIcon {
        return ZtIcon;
    }

    get ZtButtonType(): typeof ZtButtonType {
        return ZtButtonType;
    }

    get ZtHeaderTypes(): typeof ZtTableHeaderType {
        return ZtTableHeaderType;
    }
}
